/**
 * フロント側 POST /api/remove-reflection クライアント。
 *
 * - 入力: 処理済み画像の Blob (2560x1920 JPEG/PNG)
 * - 出力: クリーンアップ済み画像の Blob (同じサイズ)
 * - 失敗時: 例外を throw (呼び出し側で元 Blob にフォールバック)
 *
 * DI: fetchFn 差替え可能 (テスト用)。
 */

import type {
  ImageMimeType,
  RemoveReflectionRequest,
  RemoveReflectionResponse,
} from '@shared/types';

import { base64ToBlob, fileToBase64 } from './hero-client';

export interface CleanupClientConfig {
  readonly fetchFn?: typeof fetch;
  readonly endpoint?: string;
}

export interface CleanupResult {
  readonly blob: Blob;
  readonly mimeType: ImageMimeType;
  readonly processingTimeMs: number;
}

/**
 * Blob (通常は image-worker で処理済みの JPEG) を Worker に送って
 * 「福山工業所」文字を除去した画像を取得する。
 *
 * 失敗時は Error を throw。呼び出し側は catch して元 Blob を使う。
 */
export async function removeReflectionForBlob(
  source: Blob,
  config: CleanupClientConfig = {},
): Promise<CleanupResult> {
  const fetchFn = config.fetchFn ?? fetch;
  const endpoint = config.endpoint ?? '/api/remove-reflection';

  const sourceMime: ImageMimeType = source.type === 'image/png' ? 'image/png' : 'image/jpeg';
  const base64 = await blobToBase64(source);
  const body: RemoveReflectionRequest = { imageBase64: base64, mimeType: sourceMime };

  const res = await fetchFn(endpoint, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(`POST /api/remove-reflection returned ${res.status}`);
  }
  const data = (await res.json()) as RemoveReflectionResponse;
  const blob = base64ToBlob(data.imageBase64, data.mimeType);
  return {
    blob,
    mimeType: data.mimeType,
    processingTimeMs: data.processingTimeMs,
  };
}

// ===== 内部ヘルパ =======================================================

async function blobToBase64(blob: Blob): Promise<string> {
  // File / Blob 両対応。File は fileToBase64 をそのまま、Blob は File にラップして再利用。
  const wrapped = blob instanceof File ? blob : new File([blob], 'blob', { type: blob.type });
  return fileToBase64(wrapped);
}
